import { useEffect, useState } from "react"

import { Button } from "@coval/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverItem,
  PopoverTrigger,
} from "@coval/ui/popover"
import { Download, Loader2, Pause, Play } from "lucide-react"

import { formatTime, SECONDS_PER_HOUR } from "@/lib/audio-player-utils"

/** The controls row's own horizontal padding. */
export const CONTROLS_EDGE_PIXELS = 16

/**
 * How far in the controls row sits from each side, so its time labels stay on
 * the waveform well's edges: the speaker gutter alone, or the host's track
 * inset when it lines the well up with a timeline above.
 */
export function controlsInsetPixels(
  trackInsetPx: { leftPx: number; rightPx: number } | null | undefined,
  speakerGutter: number,
): { leadingInsetPixels: number; trailingInsetPixels: number } {
  return trackInsetPx
    ? {
        leadingInsetPixels:
          Math.max(speakerGutter, trackInsetPx.leftPx) - CONTROLS_EDGE_PIXELS,
        trailingInsetPixels: trackInsetPx.rightPx - CONTROLS_EDGE_PIXELS,
      }
    : { leadingInsetPixels: speakerGutter, trailingInsetPixels: 0 }
}

/** Space between a leading control and the elapsed time it would crowd. */
const LEADING_GAP_PIXELS = 12

/**
 * The element's laid-out width, kept current as it resizes; 0 until mounted.
 *
 * @param element - The element to measure, or null before it mounts.
 * @returns Its width in pixels.
 */
function useElementWidth(element: HTMLElement | null): number {
  const [width, setWidth] = useState(0)
  useEffect(() => {
    if (!element) {
      setWidth(0)
      return
    }
    const measure = () => setWidth(element.getBoundingClientRect().width)
    measure()
    if (typeof ResizeObserver === "undefined") {
      return
    }
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [element])
  return width
}

const PLAYBACK_RATES = [1, 1.5, 2]

function playbackRateLabel(playbackRate: number) {
  return `${playbackRate}x`
}

/** Speed button that opens a popover listing the available playback rates. */
function PlaybackRateControl({
  compact = false,
  onPlaybackRateChange,
  playbackRate,
}: {
  compact?: boolean
  onPlaybackRateChange: (playbackRate: number) => void
  playbackRate: number
}) {
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={compact ? undefined : "ghost"}
          className={
            compact
              ? "rounded-lg bg-surface-0 px-3 py-2 text-xs font-semibold text-coval-text transition-all duration-200 hover:bg-surface-1"
              : "h-6! shrink-0 rounded-full px-2 text-xs font-semibold"
          }
          aria-label={`Playback speed: ${playbackRateLabel(playbackRate)}`}
        >
          {playbackRateLabel(playbackRate)}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="top"
        className="w-24 p-1"
        role="radiogroup"
        aria-label="Playback speed"
      >
        {PLAYBACK_RATES.map((rate) => (
          <PopoverItem
            key={rate}
            role="radio"
            aria-checked={rate === playbackRate}
            checked={rate === playbackRate}
            onClick={() => {
              onPlaybackRateChange(rate)
              setOpen(false)
            }}
          >
            {playbackRateLabel(rate)}
          </PopoverItem>
        ))}
      </PopoverContent>
    </Popover>
  )
}

export function CompactAudioControls({
  isDownloading,
  isPlaying,
  onDownload,
  onPlaybackRateChange,
  onPlayPause,
  playbackRate,
}: {
  isDownloading: boolean
  isPlaying: boolean
  onDownload: () => void
  onPlaybackRateChange: (playbackRate: number) => void
  onPlayPause: () => void
  playbackRate: number
}) {
  return (
    <>
      <Button
        onClick={onPlayPause}
        className="rounded-lg bg-surface-0 p-2 text-coval-text transition-all duration-200 hover:bg-surface-1"
        aria-label={isPlaying ? "Pause" : "Play"}
      >
        {isPlaying ? <Pause size={16} /> : <Play size={16} />}
      </Button>
      <PlaybackRateControl
        compact
        onPlaybackRateChange={onPlaybackRateChange}
        playbackRate={playbackRate}
      />
      <DownloadButton
        compact
        isDownloading={isDownloading}
        onDownload={onDownload}
      />
    </>
  )
}

/** Elapsed-over-total readout for the compact player, which has no flanking labels. */
export function CompactAudioTime({
  currentTime,
  duration,
}: {
  currentTime: number
  duration: number
}) {
  return (
    <div className="relative flex items-center justify-center gap-4 pb-3 pt-3">
      <div className="pl-4 font-mono text-sm font-light text-coval-text">
        {currentTime > 0 ? `${formatTime(currentTime)} / ` : ""}
        {formatTime(duration)}
      </div>
    </div>
  )
}

/**
 * Reads out one end of the playhead range: elapsed on the left, total on the
 * right. Both ends reserve the same width, which is what keeps the control
 * cluster between them centred on the player rather than on the space left over.
 * Recordings of an hour or more format as `HH:MM:SS`, which needs the wider
 * reservation to stay clear of the cluster.
 */
function AudioTimeLabel({
  align,
  seconds,
  wide,
}: {
  align: "left" | "right"
  seconds: number | null
  wide: boolean
}) {
  return (
    <span
      className={`${wide ? "w-16" : "w-10"} shrink-0 font-mono text-xs tabular-nums text-coval-text-muted ${
        align === "left" ? "text-left" : "text-right"
      }`}
    >
      {seconds === null ? "" : formatTime(seconds)}
    </span>
  )
}

export function FullAudioControls({
  extra,
  leading,
  elapsedSeconds,
  isDownloading,
  isPlaying,
  leadingInsetPixels = 0,
  trailingInsetPixels = 0,
  onDownload,
  onPlaybackRateChange,
  onPlayPause,
  playbackRate,
  totalSeconds,
  transcriptNotice,
}: {
  extra?: React.ReactNode
  /**
   * Sits on the card's left margin, in the gutter beside the elapsed time, so
   * the time labels and the centred controls keep their places.
   */
  leading?: React.ReactNode
  elapsedSeconds: number | null
  isDownloading: boolean
  isPlaying: boolean
  /**
   * Whatever the waveform row puts to the left of the well — its speaker
   * gutter. Repeated here so both time labels stay on the well's edges and the
   * control cluster stays centred on the well rather than on the card.
   */
  leadingInsetPixels?: number
  /** Whatever the waveform row adds to the right of the well, or takes back
   *  from its padding when negative. */
  trailingInsetPixels?: number
  onDownload: () => void
  onPlaybackRateChange: (playbackRate: number) => void
  onPlayPause: () => void
  playbackRate: number
  totalSeconds: number | null
  /**
   * Set when the recording has no transcript yet, so nothing else on the page
   * carries what is said in it: the row says so under this id, and the play
   * button is described by it.
   */
  transcriptNotice?: { id: string; text: string }
}) {
  // Both ends take their width from the total, so the pair stays symmetric even
  // while the elapsed side is still reading in the shorter `MM:SS` form.
  const wideTimeLabels = (totalSeconds ?? 0) >= SECONDS_PER_HOUR
  // The elapsed label keeps to the well's edge, unless the leading control is
  // wider than the gutter it sits in; then the label moves clear of it.
  const [leadingElement, setLeadingElement] = useState<HTMLDivElement | null>(
    null,
  )
  const leadingWidth = useElementWidth(leadingElement)
  const leadingPixels = leading
    ? Math.max(leadingInsetPixels, leadingWidth + LEADING_GAP_PIXELS)
    : leadingInsetPixels

  return (
    <>
      <div
        className="relative flex items-center px-4 pb-3 pt-3"
        style={{
          ...(leadingPixels
            ? { paddingLeft: `calc(1rem + ${leadingPixels}px)` }
            : {}),
          ...(trailingInsetPixels
            ? { paddingRight: `calc(1rem + ${trailingInsetPixels}px)` }
            : {}),
        }}
      >
        {leading && (
          <div
            ref={setLeadingElement}
            className="absolute left-4 top-1/2 -translate-y-1/2"
          >
            {leading}
          </div>
        )}
        <AudioTimeLabel
          align="left"
          seconds={elapsedSeconds}
          wide={wideTimeLabels}
        />
        <div className="flex flex-1 items-center justify-center gap-2">
          <PlaybackRateControl
            onPlaybackRateChange={onPlaybackRateChange}
            playbackRate={playbackRate}
          />
          <Button
            onClick={onPlayPause}
            icon
            // `icon` already sizes this to a 28px square, which is the diameter
            // the play control wants, so it only has to ask for the round shape.
            className="shrink-0 rounded-full text-surface-default [&>svg]:fill-current"
            tooltip={isPlaying ? "Pause" : "Play"}
            aria-describedby={transcriptNotice?.id}
          >
            {/* A right-pointing triangle carries its mass behind the apex and
            low against its baseline, so a box-centred one reads as sitting
            left and low. Pause is symmetric and needs no such nudge. */}
            {isPlaying ? (
              <Pause />
            ) : (
              <Play className="translate-x-[1px] translate-y-[-1px]" />
            )}
          </Button>
          <DownloadButton
            isDownloading={isDownloading}
            onDownload={onDownload}
          />
          {extra}
        </div>
        <AudioTimeLabel
          align="right"
          seconds={totalSeconds}
          wide={wideTimeLabels}
        />
      </div>
      {transcriptNotice && (
        <p
          id={transcriptNotice.id}
          className="-mt-1 px-4 pb-3 text-center text-xs text-coval-text-muted"
        >
          {transcriptNotice.text}
        </p>
      )}
    </>
  )
}

function DownloadButton({
  compact = false,
  isDownloading,
  onDownload,
}: {
  compact?: boolean
  isDownloading: boolean
  onDownload: () => void
}) {
  return (
    <Button
      onClick={onDownload}
      disabled={isDownloading}
      variant={compact ? undefined : "ghost"}
      icon={!compact}
      className={
        compact
          ? "rounded-lg bg-surface-0 p-2 text-coval-text transition-all duration-200 hover:bg-surface-1 disabled:opacity-50"
          : "h-6! w-6! shrink-0 rounded-full p-0"
      }
      // Named the same way in both modes: `icon` is a runtime choice, and the
      // label has to hold when the button is showing its text instead.
      aria-label="Download audio"
      tooltip="Download audio"
    >
      {isDownloading ? (
        <Loader2 size={16} className="animate-spin" />
      ) : (
        <Download size={16} />
      )}
    </Button>
  )
}
